import type { ApiUpdateStop } from '#api/model/types.ts';
import type { Location } from '#domain/Location.ts';
import { isServiceOrder, type Order } from '#domain/Order.ts';
import type { Stop, StopType } from '#domain/Stop.ts';
import type { StopStatus } from '#domain/StopStatus.ts';
import type { TourDetails } from '#domain/Tour.ts';

export type TourStop = {
    id: string;
    stopId?: string;
    orderId: string;
    type: StopType;
    location: Location;
    name: string;
    category?: string;
    start: string;
    end: string;
    status?: StopStatus;
    latitude?: string;
    longitude?: string;
    comment?: string;
    stopUpdate?: ApiUpdateStop;
    group: number;
    isMisplaced?: boolean;
    customer: string;
    orderNumber?: string;
};

export const isFinishedStop = (stop: TourStop) => stop.status === 'finished';

export const anchorTourEndpointsAtEdges = (stops: TourStop[]) => {
    const tourStarts = stops.filter(stop => stop.category === 'tour-start');
    const tourEnds = stops.filter(stop => stop.category === 'tour-end');
    const otherStops = stops.filter(stop => stop.category !== 'tour-start' && stop.category !== 'tour-end');

    return [...tourStarts, ...otherStops, ...tourEnds];
};

const isOrderLockedForRemoval = (stops: TourStop[], stop: TourStop) =>
    stops.some(otherStop => otherStop.orderId === stop.orderId && isFinishedStop(otherStop));

export const canRemoveStopFromTour = (stops: TourStop[], stop: TourStop, order?: Order) => {
    if (isFinishedStop(stop)) {
        return false;
    }

    if (order?.status === 'loading' || order?.status === 'unloading' || order?.status === 'finished') {
        return false;
    }

    return !isOrderLockedForRemoval(stops, stop);
};

export const canMoveStopInTour = (_stops: TourStop[], stop: TourStop, order?: Order) =>
    (() => {
        if (stop.category === 'tour-start' || stop.category === 'tour-end') {
            return false;
        }

        if (isFinishedStop(stop) || order?.status === 'finished') {
            return false;
        }

        if (stop.type === 'load' && order?.status === 'loading') {
            return false;
        }

        if (stop.type === 'unload' && order?.status === 'unloading') {
            return false;
        }

        return !(stop.type === 'execute' && order?.status === 'executing');
    })();

export const mapToStopType = (order: Order, stop: Stop, stopType: StopType, groupId: number): TourStop => {
    return {
        id: `${stop.name}_${stopType}_${order.id}_${groupId}`,
        stopId: stop.id,
        orderId: order.id,
        orderNumber: order.number,
        group: groupId,
        type: stopType,
        location: {
            city: stop.location.city,
            street: stop.location.street,
            country: stop.location.country,
            zipCode: stop.location.zipCode,
        },
        name: stop.name,
        category: isServiceOrder(order) ? order.category : undefined,
        start: stop.startTarget,
        end: stop.endTarget,
        status: stop.status,
        latitude: stop.latitude,
        longitude: stop.longitude,
        comment: isServiceOrder(order)
            ? order.note
            : stopType === 'load'
              ? order.loadingComment
              : order.unloadingComment,
        customer: order.customerName,
    };
};

export const mapStopTypeToStop = (stop: TourStop): Stop => ({
    id: stop.stopId ?? stop.id,
    name: stop.name,
    startTarget: stop.start,
    endTarget: stop.end,
    location: stop.location,
    type: stop.type,
    status: stop.status,
    latitude: stop.latitude,
    longitude: stop.longitude,
    comment: stop.comment,
});

export const getStopsFromOrders = (selectedOrders: Order[]) => {
    return selectedOrders.flatMap((order, index) =>
        isServiceOrder(order)
            ? [mapToStopType(order, order.executionStop, 'execute', index)]
            : [
                  mapToStopType(order, order.loadingStop, 'load', index),
                  mapToStopType(order, order.unloadingStop, 'unload', index),
              ]
    );
};

export const getStopsFromTour = (tour: TourDetails, orders: Order[]) => {
    const orderGroupById = new Map(orders.map((order, index) => [order.id, index]));
    const tourOrderIds = new Set<string>();

    const tourStops = tour.stops.flatMap(stop => {
        const order = orders.find(item =>
            isServiceOrder(item)
                ? item.executionStop.id === stop.id
                : item.loadingStop.id === stop.id || item.unloadingStop.id === stop.id
        );
        if (!order) {
            return [];
        }

        tourOrderIds.add(order.id);
        const stopType: StopType = isServiceOrder(order)
            ? 'execute'
            : order.loadingStop.id === stop.id
              ? 'load'
              : 'unload';
        return [
            {
                ...mapToStopType(order, stop, stopType, orderGroupById.get(order.id) ?? 0),
            },
        ];
    });
    const addedOrders = orders.filter(order => !tourOrderIds.has(order.id));

    return [...tourStops, ...getStopsFromOrders(addedOrders)];
};
